import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { GoDotFill } from "react-icons/go";
import { AiOutlineExclamation } from "react-icons/ai";
import useDarkMode from "hooks/useDarkMode";

const PageTemplate = ({ children, title, data, desc }) => {
  const { theme } = useDarkMode();
  const isDark = theme === "dark";

  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={{
        hidden: { y: 30, opacity: 0 },
        visible: {
          y: 0,
          opacity: 1,
          transition: { duration: 0.6, ease: "easeOut" },
        },
      }}
    >
      <div
        className={`flex flex-col gap-[32px] p-[24px] md:p-[48px] rounded-[32px] transition-all duration-500 ${
          isDark
            ? "bg-[#030014]/60 backdrop-blur-xl border border-cyan-500/20 shadow-[0_0_40px_rgba(102,133,255,0.05)]"
            : "bg-white/80 backdrop-blur-md border border-cyan-100 shadow-[0_8px_32px_rgba(31,38,135,0.05)]"
        }`}
      >
        <div className="flex flex-col gap-2">
          <p
            className={`text-[36px] md:text-[48px] font-black tracking-tighter leading-tight ${
              isDark
                ? "text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.2)]"
                : "text-slate-900"
            }`}
          >
            {title}
          </p>
          <p
            className={`text-[16px] md:text-[18px]  tracking-widest uppercase ${isDark ? "text-cyan-400" : "text-cyan-600"}`}
          >
            Վերջին թարմացումը: 12/10/2025
          </p>
          <div className="h-1 w-32 bg-gradient-to-r from-cyan-500 to-emerald-400 rounded-full mt-2" />
        </div>

        <div className="flex flex-col gap-[32px]">
          <div
            className={`flex justify-start items-center p-[20px] gap-[16px] rounded-[24px] border transition-all ${
              isDark
                ? "bg-cyan-950/20 border-cyan-500/20"
                : "bg-cyan-50 border-cyan-100"
            }`}
          >
            <div
              className={`w-[48px] h-[48px] flex items-center justify-center rounded-[14px] shrink-0 ${
                isDark
                  ? "bg-cyan-500/20 text-cyan-400 shadow-[0_0_15px_rgba(102,133,255,0.3)]"
                  : "bg-cyan-100 text-cyan-600"
              }`}
            >
              <AiOutlineExclamation size={24} />
            </div>
            <span
              className={`text-[16px] md:text-[18px] leading-relaxed ${isDark ? "text-slate-300" : "text-slate-700"}`}
            >
              {desc}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-[32px]">
            {data.map((item, index) => (
              <div
                key={index}
                className={`flex flex-col gap-[16px] p-[24px] rounded-[24px] border transition-all ${
                  isDark
                    ? "bg-white/5 border-white/10 hover:border-cyan-500/30"
                    : "bg-slate-50/50 border-slate-200/50 hover:border-cyan-200"
                }`}
              >
                <p
                  className={`text-[20px] md:text-[22px] font-bold ${isDark ? "text-cyan-100" : "text-cyan-900"}`}
                >
                  <span className="opacity-50 mr-2">{index + 1}.</span>{" "}
                  {item.title}
                </p>

                {item.desc && (
                  <p
                    className={`text-[16px] md:text-[17px] leading-relaxed font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}
                  >
                    {item.desc}
                  </p>
                )}

                <div className="flex flex-col gap-3 ml-2">
                  {item.subItems.map((subItem, subIndex) => (
                    <div
                      key={subIndex}
                      className="flex items-start gap-3 group"
                    >
                      <div className="mt-1.5 shrink-0 transition-transform group-hover:scale-125">
                        <GoDotFill
                          className={
                            isDark
                              ? "text-cyan-500 drop-shadow-[0_0_5px_rgba(102,133,255,0.5)]"
                              : "text-cyan-400"
                          }
                          size={12}
                        />
                      </div>
                      <span
                        className={`text-[16px] leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}
                      >
                        {subItem}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="pt-8 text-center">
            <span
              className={`text-[20px] md:text-[24px] font-black italic tracking-tight ${
                isDark
                  ? "text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400"
                  : "text-cyan-800"
              }`}
            >
              Շնորհակալություն, որ օգտագործում եք Prognos-ը։
            </span>
          </div>
        </div>

        <div className="flex items-center justify-center pt-8 border-t border-white/5">
          {children}
        </div>
      </div>
    </motion.div>
  );
};

export default PageTemplate;
